import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Accordion from '@/components/base/Accordion';
import { guideArticles, type GuideArticle } from '@/mocks/articles';

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || 'https://vigorbuy.best';

function buildArticleJsonLd(article: GuideArticle) {
  const articleUrl = `${siteUrl}/guide/${article.slug}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${articleUrl}#article`,
        url: articleUrl,
        headline: article.title,
        description: article.description,
        datePublished: '2026-01-15',
        dateModified: '2026-06-28',
        author: {
          '@type': 'Organization',
          name: 'VigorBuy',
          url: `${siteUrl}/`,
        },
        publisher: {
          '@type': 'Organization',
          name: 'VigorBuy',
          url: `${siteUrl}/`,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/a9b8fc4c2048f16265644a3fc912425a.jpeg',
          },
        },
        isPartOf: {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          name: 'VigorBuy',
          url: `${siteUrl}/`,
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${articleUrl}#webpage`,
          url: articleUrl,
        },
      },
      ...(article.faqs.length > 0
        ? [
            {
              '@type': 'FAQPage',
              '@id': `${articleUrl}#faq`,
              url: articleUrl,
              isPartOf: { '@id': `${articleUrl}#webpage` },
              mainEntity: article.faqs.map((faq) => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            },
          ]
        : []),
    ],
  };
}

const articleCardImages: Record<string, string> = {
  'vigor-v2-article-card-01': 'https://readdy.ai/api/search-image?query=Warm%20toned%20flat%20lay%20of%20sneakers%20accessories%20bags%20and%20fashion%20items%20arranged%20on%20light%20wooden%20surface%20soft%20natural%20window%20lighting%20editorial%20product%20photography%20minimal%20composition%20earth%20tones%20elegant%20aesthetic%20no%20text%20no%20logos&width=700&height=450&seq=vigor-v2-article-card-01&orientation=landscape',
  'vigor-v2-article-card-02': 'https://readdy.ai/api/search-image?query=Organized%20desk%20workspace%20with%20laptop%20showing%20spreadsheet%20interface%20surrounded%20by%20fashion%20magazines%20and%20product%20samples%20warm%20natural%20lighting%20clean%20minimalist%20aesthetic%20editorial%20style%20beige%20tones%20no%20text%20no%20logos&width=700&height=450&seq=vigor-v2-article-card-02&orientation=landscape',
  'vigor-v2-article-card-03': 'https://readdy.ai/api/search-image?query=Secure%20shopping%20concept%20with%20padlock%20credit%20card%20and%20fashion%20items%20on%20clean%20white%20desk%20soft%20diffused%20lighting%20trust%20and%20safety%20theme%20elegant%20minimalist%20composition%20neutral%20warm%20tones%20no%20text%20no%20logos&width=700&height=450&seq=vigor-v2-article-card-03&orientation=landscape',
  'vigor-v2-article-card-04': 'https://readdy.ai/api/search-image?query=Collection%20of%20trending%20fashion%20products%20including%20premium%20sneakers%20designer%20bag%20and%20stylish%20accessories%20arranged%20on%20marble%20surface%20boutique%20lighting%20editorial%20photography%20sophisticated%20aesthetic%20warm%20tones%20no%20text%20no%20logos&width=700&height=450&seq=vigor-v2-article-card-04&orientation=landscape',
  'vigor-v2-article-card-05': 'https://readdy.ai/api/search-image?query=Split%20scene%20comparison%20showing%20organized%20spreadsheet%20dashboard%20on%20one%20side%20and%20chaotic%20multiple%20browser%20tabs%20on%20the%20other%20clean%20studio%20setting%20editorial%20photography%20concept%20warm%20lighting%20no%20text%20no%20logos&width=700&height=450&seq=vigor-v2-article-card-05&orientation=landscape',
  'vigor-v2-article-card-06': 'https://readdy.ai/api/search-image?query=Close%20up%20detail%20shot%20of%20fashion%20product%20inspection%20with%20magnifying%20glass%20checking%20fabric%20texture%20and%20stitching%20quality%20on%20warm%20wooden%20desk%20natural%20lighting%20editorial%20style%20craftsmanship%20theme%20no%20text%20no%20logos&width=700&height=450&seq=vigor-v2-article-card-06&orientation=landscape',
  'vigor-v2-article-card-07': 'https://readdy.ai/api/search-image?query=Artistic%20arrangement%20of%20premium%20sneakers%20in%20various%20colors%20and%20styles%20displayed%20on%20minimalist%20shelving%20warm%20accent%20lighting%20collector%20aesthetic%20editorial%20photography%20clean%20composition%20no%20text%20no%20logos&width=700&height=450&seq=vigor-v2-article-card-07&orientation=landscape',
  'vigor-v2-article-card-08': 'https://readdy.ai/api/search-image?query=Organized%20grid%20layout%20of%20fashion%20product%20categories%20with%20labeled%20sections%20shoes%20bags%20clothing%20accessories%20displayed%20on%20light%20background%20editorial%20catalog%20style%20clean%20composition%20warm%20tones%20no%20text%20no%20logos&width=700&height=450&seq=vigor-v2-article-card-08&orientation=landscape',
  'vigor-v2-article-card-09': 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20laptop%20displaying%20organized%20dashboard%20surrounded%20by%20fashion%20items%20coffee%20cup%20and%20plants%20bright%20natural%20window%20light%20productivity%20aesthetic%20editorial%20photography%20warm%20tones%20no%20text%20no%20logos&width=700&height=450&seq=vigor-v2-article-card-09&orientation=landscape',
};

function renderParagraph(text: string, idx: number) {
  const parts = text.split(/(\[\[.*?\]\])/g);
  return (
    <p key={idx} className="text-sm md:text-base text-foreground-600 leading-relaxed">
      {parts.map((part, i) => {
        const match = part.match(/^\[\[(.*?)\]\]$/);
        if (match) {
          const inner = match[1];
          const pipeIdx = inner.lastIndexOf('|');
          if (pipeIdx > -1) {
            const label = inner.slice(0, pipeIdx);
            const url = inner.slice(pipeIdx + 1);
            if (url.startsWith('/')) {
              return (
                <Link key={i} to={url} className="text-primary-500 hover:text-primary-600 underline underline-offset-2 font-medium">
                  {label}
                </Link>
              );
            }
          }
        }
        return <span key={i}>{part}</span>;
      })}
    </p>
  );
}

export default function GuideArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = guideArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <main>
        <Navbar />
        <section className="pt-32 pb-20 px-6 text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground-950">Article Not Found</h1>
          <Link to="/" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors">
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildArticleJsonLd(article)) }}
      />
      <Navbar />

      <article>
        <section className="pt-24 md:pt-32 pb-10 px-6 md:px-8 bg-background-100">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
              <div className="lg:col-span-3">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-foreground-500">Vigorbuy Guide 2026</span>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 mt-4 leading-tight">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-foreground-500 mt-5 max-w-xl leading-relaxed">
                  {article.description}
                </p>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mt-8">
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-background-50 rounded-lg text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-arrow-left-line"></i>
                    Back to Home
                  </Link>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-foreground-200 text-foreground-700 rounded-lg text-sm font-medium hover:border-foreground-400 transition-all whitespace-nowrap cursor-pointer"
                  >
                    Open Main Store
                    <i className="ri-external-link-line"></i>
                  </a>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src={articleCardImages[article.imageSeq] || articleCardImages['vigor-v2-article-card-01']}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
              {[
                { value: article.readTime, label: 'Read Time' },
                { value: 'Jun 2026', label: 'Last Updated' },
                { value: '1,200+', label: 'Words' },
                { value: 'Expert', label: 'Rated Guide' },
              ].map((stat) => (
                <div key={stat.label} className="bg-white rounded-xl p-5 text-center border border-background-200/60">
                  <span className="font-heading text-2xl md:text-3xl font-bold text-primary-500 block">{stat.value}</span>
                  <span className="text-xs text-foreground-500 mt-1">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16 px-6 md:px-8 bg-background-50">
          <div className="max-w-[1280px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
              <div className="lg:col-span-3">
                <div className="space-y-10 max-w-none">
                  {article.sections.map((section, sIdx) => (
                    <div key={sIdx}>
                      {section.heading && (
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-1">
                          {section.heading}
                        </h2>
                      )}
                      {section.subheading && (
                        <p className="text-lg font-heading italic text-foreground-500 mb-4">{section.subheading}</p>
                      )}
                      <div className={`space-y-5 ${section.heading ? 'mt-5' : ''}`}>
                        {section.paragraphs.map((p, pIdx) => renderParagraph(p, pIdx))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-1">
                <div className="sticky top-24 space-y-6">
                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h4 className="text-sm font-semibold text-foreground-950 mb-3">Quick Links</h4>
                    <ul className="space-y-2.5">
                      <li>
                        <Link to="/" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-2">
                          <i className="ri-home-line text-sm"></i>
                          Back to Homepage
                        </Link>
                      </li>
                      <li>
                        <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-2">
                          <i className="ri-footprint-line text-sm"></i>
                          Browse Shoes
                        </a>
                      </li>
                      <li>
                        <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-2">
                          <i className="ri-external-link-line text-sm"></i>
                          Open Main Store
                        </a>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-accent-100 rounded-xl p-5 border border-accent-200/60">
                    <h4 className="text-sm font-semibold text-accent-800 mb-2">Pro Tip</h4>
                    <p className="text-xs text-accent-700 leading-relaxed">
                      Bookmark the main <Link to="/" className="underline underline-offset-2 font-medium">Vigorbuy homepage</Link> for instant access to all 15 categories. Daily updates mean there is always something new to discover.
                    </p>
                  </div>

                  <div className="bg-background-100 rounded-xl p-5 border border-background-200/60">
                    <h4 className="text-sm font-semibold text-foreground-950 mb-3">More Guides</h4>
                    <ul className="space-y-2">
                      {guideArticles.filter(a => a.slug !== slug).slice(0, 5).map((a) => (
                        <li key={a.slug}>
                          <Link
                            to={`/guide/${a.slug}`}
                            className="text-xs text-foreground-500 hover:text-primary-500 transition-colors line-clamp-1"
                          >
                            {a.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {article.faqs.length > 0 && (
          <section className="py-12 md:py-16 px-6 md:px-8 bg-background-100">
            <div className="max-w-[1280px] mx-auto">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">
                Frequently Asked Questions
              </h2>
              <Accordion items={article.faqs} />
            </div>
          </section>
        )}

        <section className="py-12 md:py-16 px-6 md:px-8 bg-background-50">
          <div className="max-w-[1280px] mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950">
              Get the Official Vigorbuy Sheet
            </h2>
            <p className="text-sm text-foreground-500 mt-3 max-w-md mx-auto">
              Ready to start shopping smarter? Access the complete Vigorbuy Spreadsheet with all 15 categories.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-7">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2.5 px-8 py-3.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <i className="ri-arrow-right-line text-lg"></i>
              </a>
              <Link
                to="/"
                className="inline-flex items-center gap-2.5 px-8 py-3.5 border border-foreground-200 text-foreground-700 rounded-full text-sm font-medium hover:border-foreground-400 transition-all whitespace-nowrap cursor-pointer"
              >
                Vigorbuy Homepage
                <i className="ri-home-line text-base"></i>
              </Link>
            </div>
          </div>
        </section>
      </article>

      <Footer />
    </main>
  );
}